import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import StatusBadge from "@/components/base/StatusBadge";
import DataTable, { type DataColumn } from "@/components/base/DataTable";
import ConfirmDialog from "@/components/base/ConfirmDialog";
import AdminPageHeader from "../../components/AdminPageHeader";
import AdminToolbar from "../../components/AdminToolbar";
import AdminFilterTabs from "../../components/AdminFilterTabs";
import { useAuth } from "@/context/AuthContext";
import {
  bulkDeleteAdminOrders,
  createAdminOrder,
  deleteAdminOrder,
  getAdminOrders,
  updateAdminOrder,
  type OrderInput,
} from "@/services/adminApi";
import type { OrderRecord } from "@/types";

const statusTabs = [
  { value: "all", label: "All" },
  { value: "paid", label: "Paid" },
  { value: "pending", label: "Pending" },
  { value: "refunded", label: "Refunded" },
  { value: "failed", label: "Failed" },
];

const STATUS_OPTIONS: OrderRecord["status"][] = ["paid", "pending", "refunded", "failed"];

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none";
const labelClass = "mb-1.5 block text-xs font-semibold uppercase tracking-wide text-foreground-500";

interface DraftState {
  orderNo: string;
  customer: string;
  planName: string;
  amount: string;
  status: OrderRecord["status"];
}

const emptyDraft: DraftState = { orderNo: "", customer: "", planName: "", amount: "", status: "pending" };

export default function AdminCrmOrders() {
  const { token } = useAuth();
  const [rows, setRows] = useState<OrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<OrderRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [selected, setSelected] = useState<string[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getAdminOrders(token)
      .then((o) => {
        if (!active) return;
        setRows(o);
        setSelected([]);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2200);
  };

  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3200);
  };

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return rows.filter((r) => {
      const matchesStatus = status === "all" || r.status === status;
      const matchesQuery =
        !q || r.orderNo.toLowerCase().includes(q) || r.customer.toLowerCase().includes(q) || r.planName.toLowerCase().includes(q);
      return matchesStatus && matchesQuery;
    });
  }, [rows, query, status]);

  const totals = useMemo(() => {
    const paid = rows.filter((r) => r.status === "paid").reduce((s, r) => s + r.amount, 0);
    const pending = rows.filter((r) => r.status === "pending").reduce((s, r) => s + r.amount, 0);
    const refunded = rows.filter((r) => r.status === "refunded").reduce((s, r) => s + r.amount, 0);
    return { paid, pending, refunded };
  }, [rows]);

  const toggleRow = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const toggleAll = (keys: string[], checked: boolean) =>
    setSelected((prev) => {
      const set = new Set(prev);
      keys.forEach((k) => (checked ? set.add(k) : set.delete(k)));
      return Array.from(set);
    });

  const resetForm = () => {
    setDraft(emptyDraft);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const orderNo = draft.orderNo.trim();
    if (!orderNo) return;
    const payload: OrderInput = {
      orderNo,
      customer: draft.customer.trim() || undefined,
      planName: draft.planName.trim() || undefined,
      amount: draft.amount ? Number(draft.amount) : 0,
      status: draft.status,
    };
    setSaving(true);

    if (editingId) {
      const ok = await updateAdminOrder(editingId, payload, token);
      if (!ok) {
        setSaving(false);
        flashError("Couldn't save that order. Please try again.");
        return;
      }
      setRows((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...payload, amount: payload.amount ?? r.amount } : r)));
      flash(`Order ${orderNo} updated.`);
    } else {
      const created = await createAdminOrder(payload, token);
      if (!created) {
        setSaving(false);
        flashError("Couldn't create that order. Please try again.");
        return;
      }
      setRows((prev) => [created, ...prev]);
      flash(`Order ${orderNo} created.`);
    }

    setSaving(false);
    resetForm();
  };

  const startEdit = (row: OrderRecord) => {
    setEditingId(row.id);
    setDraft({ orderNo: row.orderNo, customer: row.customer, planName: row.planName, amount: String(row.amount), status: row.status });
    setShowForm(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const row = pendingDelete;
    setDeleting(true);
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => r.id !== row.id));
    const ok = await deleteAdminOrder(row.id, token);
    setDeleting(false);
    setPendingDelete(null);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't remove that order. Please try again.");
      return;
    }
    setSelected((prev) => prev.filter((k) => k !== row.id));
    if (editingId === row.id) resetForm();
    flash("Order removed.");
  };

  const confirmBulkDelete = async () => {
    if (!selected.length) return;
    setBulkBusy(true);
    const ids = [...selected];
    const snapshot = rows;
    setRows((prev) => prev.filter((r) => !ids.includes(r.id)));
    const ok = await bulkDeleteAdminOrders(ids, token);
    setBulkBusy(false);
    setBulkOpen(false);
    if (!ok) {
      setRows(snapshot);
      flashError("Couldn't delete the selected orders. Please try again.");
      return;
    }
    setSelected([]);
    flash(`${ids.length} order${ids.length > 1 ? "s" : ""} deleted.`);
  };

  const columns: DataColumn<OrderRecord>[] = [
    { key: "orderNo", header: "Order", render: (r) => <span className="font-mono text-xs font-semibold text-foreground-800">{r.orderNo}</span>, nowrap: true },
    { key: "customer", header: "Customer", render: (r) => <span className="font-medium text-foreground-900">{r.customer}</span> },
    { key: "planName", header: "Item", render: (r) => <span className="text-foreground-700">{r.planName}</span> },
    { key: "amount", header: "Amount", align: "right", render: (r) => <span className="font-semibold text-foreground-900">₹{r.amount.toLocaleString("en-IN")}</span>, nowrap: true },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} />, nowrap: true },
    { key: "createdAt", header: "Date", render: (r) => <span className="text-foreground-500">{r.createdAt}</span>, nowrap: true },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => startEdit(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
            aria-label="Edit order"
          >
            <i className="ri-edit-line"></i>
          </button>
          <button
            type="button"
            onClick={() => setPendingDelete(r)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-secondary-300 text-secondary-700 hover:bg-secondary-50 cursor-pointer"
            aria-label="Delete order"
          >
            <i className="ri-delete-bin-line"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Seo title="CRM Orders" path="/admin/crm/orders" />

      <AdminPageHeader
        icon="ri-file-list-3-line"
        title="CRM orders"
        subtitle="Every purchase made across plans, add-ons and advertising."
        actions={
          <>
            {actionError ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
                <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
              </span>
            ) : notice ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            ) : null}
            <Link
              to="/admin/crm/invoices"
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bill-line"></i> View invoices
            </Link>
            <button
              type="button"
              onClick={() => (showForm ? resetForm() : setShowForm(true))}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className={showForm ? "ri-close-line" : "ri-add-line"}></i> {showForm ? "Cancel" : "New order"}
            </button>
          </>
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Collected</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-accent-700">₹{totals.paid.toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Pending</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-primary-700">₹{totals.pending.toLocaleString("en-IN")}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Refunded</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-secondary-700">₹{totals.refunded.toLocaleString("en-IN")}</p>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50 p-4 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label htmlFor="ordNo" className={labelClass}>Order no. *</label>
            <input id="ordNo" value={draft.orderNo} onChange={(e) => setDraft((d) => ({ ...d, orderNo: e.target.value }))} placeholder="MYP-1402" className={inputClass} />
          </div>
          <div>
            <label htmlFor="ordCustomer" className={labelClass}>Customer</label>
            <input id="ordCustomer" value={draft.customer} onChange={(e) => setDraft((d) => ({ ...d, customer: e.target.value }))} placeholder="Name" className={inputClass} />
          </div>
          <div>
            <label htmlFor="ordItem" className={labelClass}>Item</label>
            <input id="ordItem" value={draft.planName} onChange={(e) => setDraft((d) => ({ ...d, planName: e.target.value }))} placeholder="Premium — Annual" className={inputClass} />
          </div>
          <div>
            <label htmlFor="ordAmount" className={labelClass}>Amount (₹)</label>
            <input id="ordAmount" type="number" min="0" value={draft.amount} onChange={(e) => setDraft((d) => ({ ...d, amount: e.target.value }))} placeholder="0" className={inputClass} />
          </div>
          <div>
            <label htmlFor="ordStatus" className={labelClass}>Status</label>
            <select id="ordStatus" value={draft.status} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as OrderRecord["status"] }))} className={inputClass}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div className="sm:col-span-2 lg:col-span-5 flex justify-end">
            <button
              type="submit"
              disabled={saving || !draft.orderNo.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              {editingId ? "Save changes" : "Save order"}
            </button>
          </div>
        </form>
      )}

      <div className="mt-6">
        <AdminToolbar query={query} onQuery={setQuery} placeholder="Search orders…">
          <AdminFilterTabs options={statusTabs} value={status} onChange={setStatus} />
        </AdminToolbar>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-foreground-600">
          <span className="font-semibold text-foreground-950">{filtered.length}</span> of {rows.length} orders
        </p>
        <div className="flex items-center gap-3">
          {selected.length > 0 && <span className="text-sm font-medium text-foreground-700">{selected.length} selected</span>}
          <button
            type="button"
            onClick={() => setBulkOpen(true)}
            disabled={selected.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-secondary-300 px-3.5 py-2 text-sm font-semibold text-secondary-800 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-delete-bin-6-line"></i> Delete selected
          </button>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
        ) : error ? (
          <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center text-sm text-secondary-900">
            Couldn't load orders. Please refresh.
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(r) => r.id}
            minWidth="1000px"
            selectable
            selectedKeys={selected}
            onToggleRow={toggleRow}
            onToggleAll={toggleAll}
          />
        )}
      </div>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete this order?"
        message={pendingDelete ? `Order ${pendingDelete.orderNo} will be permanently removed. This cannot be undone.` : ""}
        confirmLabel="Delete order"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={bulkOpen}
        title={`Delete ${selected.length} order${selected.length > 1 ? "s" : ""}?`}
        message="The selected orders will be permanently removed. This cannot be undone."
        confirmLabel="Delete selected"
        busy={bulkBusy}
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkOpen(false)}
      />
    </>
  );
}